import { useContext } from 'react';
import { MenuItem, Grid, Button, Table, TableBody, TableCell, TableHead, TableRow, Link, Typography } from '@mui/material';
import { ErrorMessage, Field, FieldProps, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { appUserContext, customerApplicationContext } from 'helpers/services';
import { CustomQuillEditor, DeleteIconButton, MachineAutocomplete } from 'components';
import { customerNewTicketContext } from '../CustomerNewTicketContext';
import { IMachineSearch } from 'components/MachineAutocomplete/MachineAutocomplete';
import { TicketOrigin } from 'helpers/Enums';
import { LocalizationIds } from 'helpers/Localizations';
import { useIntl } from 'react-intl';
import { localeManagerContext } from 'helpers/Locale';
import Moment from 'react-moment';
import * as yup from 'yup';


let NewTicketForm = () => {
    let { formatMessage } = useIntl();
    let appService = useContext(customerApplicationContext);
    let service = useContext(customerNewTicketContext);
    let { currentUser } = useContext(appUserContext);
    let localeManager = useContext(localeManagerContext);

    type NewTicket = yup.InferType<typeof service.newTicketSchema>;

    let form = useFormikContext<NewTicket>();


    let machineAutocompleteHandler = (value: IMachineSearch) => {
        if (value.linkedTo === "Order")
            service.orderSelectionHandler({ ...value.id, orderTitle: "" });

        if (value.linkedTo === "ProductionOrder")
            service.workOrderSelectionHandler({ ...value.id });

        if (form.values.customerId === 0) {
            form.setFieldValue('customerId', value.customerId || 0)
            form.setFieldValue('customerName', value.customerName || '')
            form.setFieldValue('customerCountryCode', value.customerCountryCode || '')
            form.setFieldValue('customerCountryName', value.customerCountryName || '')
        }

    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Field
                    select
                    component={TextField}
                    label={formatMessage({ id: LocalizationIds.Category })}
                    name="ticketSubCategoryId"
                >
                    {appService.ticketSubCategories.map(subcategory =>
                        <MenuItem key={subcategory.id} value={subcategory.id}>{subcategory.categoryNames[localeManager.currentLocale]} / {subcategory.names[localeManager.currentLocale]}</MenuItem>
                    )}
                </Field>
            </Grid>
            <Grid item xs={12}>
                <Field
                    component={TextField}
                    label={formatMessage({ id: LocalizationIds.Title })}
                    name="title"
                />
            </Grid>
            <Grid item xs={12} style={{ height: 186 }}>
                <Field name="message">
                    {({ field }: FieldProps) =>
                        <div data-text-editor="name">
                            <CustomQuillEditor
                                style={{ height: 136 }}
                                value={field.value}
                                onChange={field.onChange(field.name)}
                            />
                        </div>
                    }
                </Field>
            </Grid>
            <Grid item xs>
                <ErrorMessage name="message">
                    {msg => <Typography variant="caption" style={{ color: '#f44336' }}>{msg}</Typography>}
                </ErrorMessage>
            </Grid>
            {currentUser.customerClaims?.isAdmin === true &&
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        select
                        label={formatMessage({ id: LocalizationIds.Reporter })}
                        name="reporterUserName"
                    >
                        {service.reporters.map(reporter =>
                            <MenuItem key={reporter.value} value={reporter.value}>{reporter.label}</MenuItem>
                        )}
                    </Field>
                </Grid>
            }

            <Grid item xs={12}>
                <MachineAutocomplete
                    onChange={(value) => machineAutocompleteHandler(value)}
                    typology={TicketOrigin.Customer}
                    inputLabel={formatMessage({ id: LocalizationIds.SearchMachineExtended })}
                    customerId={form.values.customerId === null || form.values.customerId === 0 ? undefined : form.values.customerId}
                    linkedTo={service.selectedOrders.length !== 0 ? 'Order' : (service.selectedWorkOrders.length !== 0 ? 'ProductionOrder' : undefined)}
                />
            </Grid>
            <Grid item xs={12}>
                {service.selectedOrders.length !== 0 &&
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{formatMessage({ id: LocalizationIds.Order })}</TableCell>
                                <TableCell>{formatMessage({ id: LocalizationIds.Description })}</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {service.selectedOrders.map((order, index) =>
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant="body2">{order.orderType}{order.orderNumber}</Typography>
                                        {order.orderDate &&
                                            <Typography variant="caption">
                                                <Moment date={order.orderDate} format="LL" />
                                            </Typography>
                                        }
                                    </TableCell>
                                    <TableCell>{order.orderTitle}</TableCell>
                                    <TableCell align="right">
                                        <DeleteIconButton onClick={() => service.orderSelectionHandler({ ...order, orderTitle: '' })} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {service.selectedOrders.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={3}>{formatMessage({ id: LocalizationIds.NoSelectedOrders })}Nessun ordine selezionato</TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                }
                {service.selectedWorkOrders.length !== 0 &&
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{formatMessage({ id: LocalizationIds.WorkOrder })}</TableCell>
                                <TableCell>{formatMessage({ id: LocalizationIds.Order })}</TableCell>
                                <TableCell>{formatMessage({ id: LocalizationIds.Description })}</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {service.selectedWorkOrders.map((workOrder, index) =>
                                <TableRow key={index}>
                                    <TableCell>{workOrder.workOrderNumber}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{workOrder.orderType}{workOrder.orderNumber}</Typography>
                                        {workOrder.orderDate &&
                                            <Typography variant="caption">
                                                <Moment date={workOrder.orderDate} format="LL" />
                                            </Typography>
                                        }
                                    </TableCell>
                                    <TableCell>{workOrder.articleModel} {workOrder.articleVersion} {workOrder.articleDescription}</TableCell>
                                    <TableCell align="right">
                                        <DeleteIconButton onClick={() => service.workOrderSelectionHandler(workOrder)} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {service.selectedWorkOrders.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={3}>{formatMessage({ id: LocalizationIds.NoSelectedOrders })}Nessun ordine selezionato</TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                }
            </Grid>
            <Grid item xs />
            <Grid item>
                <Button type="submit" variant="contained">
                    {formatMessage({ id: LocalizationIds.CreateTicket })}
                </Button>
            </Grid>
        </Grid>
    );
}

export default NewTicketForm;